import { useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { Helmet } from "react-helmet";
import { useQueryClient } from "@tanstack/react-query";
import { Spin, Alert, Input } from "antd";
import { FormOutlined } from "@ant-design/icons";
import { AuthContext } from "../context/AuthProvider";
import { APP_NAME } from "../helpers/constants";
import {
  device,
  LAYOUT_CONSTANTS,
  DEFAULT_SCREEN_SIZES,
} from "../lib/breakpoints";
import { userKeys } from "../lib/queryKeys";
import { submitOnboardingSurvey } from "../lib/api/onboardingSurveyApi";
import Button from "../components/Button";
import PageHeader from "../components/PageHeader";
import SectionCard from "../components/shared/SectionCard";

const { TextArea: AntTextArea } = Input;

const RATING_KEYS = [
  "easyToNavigate",
  "leoHelpful",
  "setupStraightforward",
  "dashboardClear",
  "rentFeaturesMeetNeeds",
  "trustWithData",
  "valueForPrice",
];

const QUESTIONS = [
  {
    key: "easyToNavigate",
    text: "LandlordOps is easy to navigate and understand.",
  },
  {
    key: "leoHelpful",
    text: "Leo (the AI assistant) is helpful for my workflow.",
  },
  {
    key: "setupStraightforward",
    text: "Setting up properties and units was straightforward.",
  },
  {
    key: "dashboardClear",
    text: "The dashboard gives me a clear view of my portfolio.",
  },
  {
    key: "rentFeaturesMeetNeeds",
    text: "Rent collection and payment tools meet my needs.",
  },
  {
    key: "trustWithData",
    text: "I trust LandlordOps with my landlord data.",
  },
  {
    key: "valueForPrice",
    text: "LandlordOps delivers good value for the price.",
  },
];

const SCALE_HINTS = ["Strongly disagree", "", "", "", "Strongly agree"];

function OnboardingSurveyPage() {
  const { user, userInfo, isUserInfoLoading } = useContext(AuthContext);
  const queryClient = useQueryClient();
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [ratings, setRatings] = useState(() =>
    Object.fromEntries(RATING_KEYS.map((k) => [k, null]))
  );
  const [missingOrWants, setMissingOrWants] = useState("");
  const [favoriteAspects, setFavoriteAspects] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [doneLocal, setDoneLocal] = useState(false);

  const alreadySubmitted = !!userInfo?.onboardingSurveySubmittedAt;

  useEffect(() => {
    if (!user) return;
    const name =
      userInfo?.displayName ||
      userInfo?.name ||
      user.displayName ||
      user.email?.split("@")[0] ||
      "";
    setDisplayName(name);
    setEmail(user.email || userInfo?.email || "");
  }, [user, userInfo]);

  const allRated = useMemo(() => RATING_KEYS.every((k) => ratings[k] != null), [
    ratings,
  ]);

  const handleSetRating = (key, value) => {
    setRatings((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!allRated) {
      setError("Please rate each statement from 1 to 5.");
      return;
    }
    if (!email?.trim()) {
      setError("We need your account email to submit.");
      return;
    }
    try {
      setSubmitting(true);
      const res = await submitOnboardingSurvey({
        displayName: displayName.trim() || email.split("@")[0],
        email: email.trim(),
        ratings: {
          easyToNavigate: ratings.easyToNavigate,
          leoHelpful: ratings.leoHelpful,
          setupStraightforward: ratings.setupStraightforward,
          dashboardClear: ratings.dashboardClear,
          rentFeaturesMeetNeeds: ratings.rentFeaturesMeetNeeds,
          trustWithData: ratings.trustWithData,
          valueForPrice: ratings.valueForPrice,
        },
        missingOrWants: missingOrWants.trim(),
        favoriteAspects: favoriteAspects.trim(),
      });
      if (user?.uid) {
        queryClient.invalidateQueries({ queryKey: userKeys.user(user.uid) });
      }
      if (res.alreadySubmitted) {
        setDoneLocal(true);
      } else {
        setDoneLocal(true);
      }
    } catch (err) {
      setError(err?.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) {
    return null;
  }

  if (isUserInfoLoading) {
    return (
      <SurveyContainer>
        <LoadingWrap>
          <Spin size="large" />
        </LoadingWrap>
      </SurveyContainer>
    );
  }

  const showThanks = alreadySubmitted || doneLocal;

  return (
    <SurveyContainer>
      <Helmet>
        <title>Your feedback · {APP_NAME}</title>
        <meta
          name="description"
          content="Help us improve LandlordOps with a short first-impressions survey."
        />
      </Helmet>
      <SurveyPageHeader
        icon={FormOutlined}
        title="First impressions survey"
        subtitle={
          showThanks
            ? "Thanks for taking the time to share your perspective."
            : "A short check-in so we can prioritize what to build next. Your answers stay with your account."
        }
      />
      <SurveyContent>
        {showThanks ? (
          <SectionCard title="Thank You!">
            <ThanksBlock>
              <ThanksBody>
                We’ve saved your feedback. It goes straight to the product team
                to prioritize what we build next.
              </ThanksBody>
              <SurveyThanksDashboardLink
                as={Link}
                to="/dashboard"
                variant="primary"
                rounded="slight"
                size="medium"
                width="100%"
              >
                Back to dashboard
              </SurveyThanksDashboardLink>
            </ThanksBlock>
          </SectionCard>
        ) : (
          <FormStack onSubmit={handleSubmit} noValidate>
            <SectionCard title="Your account">
              <Field>
                <Label htmlFor="survey-name">Name</Label>
                <StyledInput
                  id="survey-name"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  autoComplete="name"
                  size="large"
                />
              </Field>
              <Field>
                <Label htmlFor="survey-email">Email</Label>
                <StyledInput
                  id="survey-email"
                  type="email"
                  value={email}
                  required
                  readOnly
                  aria-readonly="true"
                  size="large"
                />
                <Hint>Read-only so it always matches your login.</Hint>
              </Field>
            </SectionCard>

            <SectionCard title="Rate each statement">
              <ScaleExplainer>
                1 = strongly disagree · 5 = strongly agree
              </ScaleExplainer>
              <LikertList>
                {QUESTIONS.map((q) => (
                  <LikertBlock key={q.key}>
                    <Statement>{q.text}</Statement>
                    <ScaleRow>
                      {SCALE_HINTS.map((hint, i) => (
                        <ScaleHint key={`h-${q.key}-${i}`}>{hint}</ScaleHint>
                      ))}
                    </ScaleRow>
                    <ButtonsRow role="group" aria-label={q.text}>
                      {[1, 2, 3, 4, 5].map((n) => (
                        <LikertBtn
                          type="button"
                          key={n}
                          $active={ratings[q.key] === n}
                          onClick={() => handleSetRating(q.key, n)}
                        >
                          {n}
                        </LikertBtn>
                      ))}
                    </ButtonsRow>
                    <MobileScaleHint>1 = disagree · 5 = agree</MobileScaleHint>
                  </LikertBlock>
                ))}
              </LikertList>
            </SectionCard>

            <SectionCard title="Anything else?">
              <Field>
                <Label htmlFor="survey-missing">
                  What’s missing, or what would you most like to see next?
                </Label>
                <StyledTextArea
                  id="survey-missing"
                  value={missingOrWants}
                  onChange={(e) => setMissingOrWants(e.target.value)}
                  rows={4}
                  placeholder="Optional: be as specific as you like."
                />
              </Field>
              <Field $flush>
                <Label htmlFor="survey-fav">
                  What are your favorite aspects of the product so far?
                </Label>
                <StyledTextArea
                  id="survey-fav"
                  value={favoriteAspects}
                  onChange={(e) => setFavoriteAspects(e.target.value)}
                  rows={4}
                  placeholder="Optional: features, workflows, or anything that stood out."
                />
              </Field>
            </SectionCard>

            <SubmitFooter>
              {error ? (
                <Alert
                  type="error"
                  showIcon
                  message={error}
                  role="alert"
                  style={{ marginBottom: 16 }}
                />
              ) : null}
              <Actions>
                <SurveyPrimaryCta
                  type="submit"
                  variant="primary"
                  rounded="slight"
                  size="medium"
                  isLoading={submitting}
                  disabled={submitting || !allRated}
                  width="auto"
                >
                  Submit feedback
                </SurveyPrimaryCta>
              </Actions>
            </SubmitFooter>
          </FormStack>
        )}
      </SurveyContent>
    </SurveyContainer>
  );
}

export default OnboardingSurveyPage;

const SurveyPageHeader = styled(PageHeader)`
  width: 100%;
  box-sizing: border-box;

  /* Match PageHeader / Leases-style titles; globals.css span rules otherwise win on desktop */
  h1,
  h1 span {
    color: #ffffff !important;
    font-family: var(--font-primary) !important;
    font-weight: 700 !important;
    font-size: 2.25rem !important;
    line-height: 1.2 !important;
    letter-spacing: -0.02em !important;
  }

  h1 {
    display: flex !important;
    align-items: center !important;
  }

  @media (max-width: 1200px) {
    h1,
    h1 span {
      font-size: 2.125rem !important;
    }
  }

  @media (max-width: 768px) {
    h1,
    h1 span {
      font-size: 2rem !important;
      line-height: 1.15 !important;
    }
  }

  @media (max-width: ${DEFAULT_SCREEN_SIZES.DESKTOP - 1}px) {
    border-radius: 16px;
    padding: 1.25rem 1rem;
    margin-bottom: 1rem;
  }
`;

const SurveyContainer = styled.div`
  /* AppLayout uses text-align: center; reset so cards and forms align like Leases */
  text-align: left;
  padding: 2rem;
  background: var(--color-bg-primary);
  min-height: 100vh;
  width: ${LAYOUT_CONSTANTS.DESKTOP_CONTENT_WIDTH};
  margin: 0 auto;
  box-sizing: border-box;

  /* Mobile/tablet: horizontal gutters like Dashboard (nav uses ~1rem; content aligns) */
  @media (max-width: ${DEFAULT_SCREEN_SIZES.DESKTOP - 1}px) {
    width: 100%;
    max-width: 100%;
    margin: 0;
    padding: 1rem;
    min-width: 0;
    min-height: 100vh;
  }
`;

const SurveyContent = styled.div`
  width: 100%;
  min-height: 200px;

  @media (max-width: ${DEFAULT_SCREEN_SIZES.DESKTOP - 1}px) {
    padding: 0;
  }

  .section-card {
    margin-bottom: 1.5rem;
  }

  .section-card:last-of-type {
    margin-bottom: 0;
  }
`;

const SubmitFooter = styled.div`
  width: 100%;
  padding: 1.5rem 0 2rem;
  margin-top: 0.25rem;
  border-top: 1px solid var(--color-border-light);
  box-sizing: border-box;
`;

const FormStack = styled.form`
  display: flex;
  flex-direction: column;
  gap: 0;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
`;

const LoadingWrap = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 320px;
  padding: 3rem;
`;

/** Overrides globals + link resets: color/bg on <a>, and reliable size/radius (match Ant primary ~12px) */
const SurveyPrimaryCta = styled(Button)`
  box-sizing: border-box !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  -webkit-appearance: none !important;
  appearance: none !important;
  text-decoration: none !important;
  border: none !important;
  min-height: 46px !important;
  padding: 0 1.25rem !important;
  border-radius: 12px !important;
  font-family: var(--font-secondary) !important;
  font-size: 1rem !important;
  font-weight: 600 !important;
  line-height: 1.2 !important;

  &&,
  &&:link,
  &&:visited {
    background-color: var(--color-primary) !important;
    color: #ffffff !important;
  }

  &&:hover,
  &&:focus-visible {
    background-color: var(--color-primary-dark) !important;
    color: #ffffff !important;
  }

  &&:active {
    background-color: var(--color-primary-darker) !important;
    color: #ffffff !important;
  }

  &&[disabled] {
    background-color: var(--color-bg-tertiary) !important;
    color: var(--color-text-light) !important;
    border: 1px solid var(--color-border) !important;
    opacity: 0.85;
    cursor: not-allowed;
  }
`;

/** Success state: full-width up to a comfortable max so the CTA matches other primary actions */
const SurveyThanksDashboardLink = styled(SurveyPrimaryCta)`
  width: 100% !important;
  max-width: 22rem;
  min-height: 48px !important;
  padding: 14px 1.75rem !important;
`;

const ThanksBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const ThanksBody = styled.p`
  margin: 0 0 1.5rem;
  font-size: 1rem;
  line-height: 1.6;
  color: var(--color-text-secondary);
  font-family: var(--font-secondary);
  max-width: 36rem;
`;

const ScaleExplainer = styled.p`
  margin: 0 0 2rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--color-text-secondary);
  font-family: var(--font-secondary);
  line-height: 1.5;
`;

const Field = styled.div`
  margin-bottom: ${(p) => (p.$flush ? 0 : "1rem")};
`;

const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 8px;
  color: var(--color-text-primary);
  font-family: var(--font-secondary);
`;

const StyledInput = styled(Input)`
  font-family: var(--font-secondary) !important;

  &.ant-input[disabled],
  &.ant-input[readonly] {
    background: var(--color-bg-tertiary) !important;
    color: var(--color-text-secondary) !important;
  }
`;

const StyledTextArea = styled(AntTextArea)`
  font-family: var(--font-secondary) !important;
  resize: vertical;
`;

const Hint = styled.p`
  margin: 6px 0 0;
  font-size: 0.75rem;
  color: var(--color-text-muted);
  font-family: var(--font-secondary);
`;

const LikertList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;

  @media ${device.mobile} {
    gap: 1.625rem;
  }
`;

const LikertBlock = styled.div`
  padding-bottom: 1.5rem;
  margin: 0;
  border-bottom: 1px solid var(--color-border-light);

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`;

const Statement = styled.p`
  margin: 0 0 1rem;
  font-size: 1rem;
  line-height: 1.6;
  color: var(--color-text-primary);
  font-weight: 600;
  font-family: var(--font-secondary);
`;

const ScaleRow = styled.div`
  display: none;
  @media ${device.tablet}, ${device.desktop} {
    display: flex;
    justify-content: space-between;
    font-size: 0.6875rem;
    color: var(--color-text-muted);
    margin-bottom: 12px;
    padding: 0 6px;
    font-family: var(--font-secondary);
    line-height: 1.35;
  }
`;

const ScaleHint = styled.span`
  flex: 1;
  text-align: center;
  &:first-child {
    text-align: left;
  }
  &:last-child {
    text-align: right;
  }
`;

const MobileScaleHint = styled.p`
  margin: 12px 0 0;
  font-size: 0.72rem;
  color: var(--color-text-muted);
  text-align: center;
  font-family: var(--font-secondary);
  line-height: 1.4;

  @media ${device.tablet}, ${device.desktop} {
    display: none;
  }
`;

const ButtonsRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 2px;
`;

const LikertBtn = styled.button`
  flex: 1;
  min-width: 44px;
  min-height: 52px;
  border-radius: 12px;
  border: 2px solid
    ${(p) => (p.$active ? "var(--color-primary)" : "var(--color-border-light)")};
  background: ${(p) =>
    p.$active ? "var(--color-primary-glow)" : "var(--color-bg-secondary)"};
  color: var(--color-text-primary);
  font-weight: 700;
  font-size: 1rem;
  font-family: var(--font-secondary);
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease,
    box-shadow 0.15s ease;
  box-shadow: ${(p) =>
    p.$active ? "0 2px 8px rgba(44, 95, 45, 0.12)" : "none"};

  &:hover {
    border-color: var(--color-primary-dark);
  }

  &:focus-visible {
    outline: 2px solid var(--color-primary);
    outline-offset: 2px;
  }
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-top: 8px;
  padding-top: 8px;
  width: 100%;
`;
